// src/frontend/data/books/BlockchainFundamentals/data/cap10.js

const CAP10 = {
  id: 10,
  slug: "cap10-polkadot-dot",
  title: "Capítulo 10 — Polkadot (DOT): red multicadena, parachains y Polkadot 2.0",
  sections: [
    {
      heading: "¿Qué es Polkadot (DOT)?",
      text: [
        "Polkadot (DOT) es un proyecto blockchain diseñado para conectar múltiples blockchains entre sí de forma segura, escalable y eficiente.",
        "Su objetivo es permitir que distintas cadenas (con reglas y funciones diferentes) se comuniquen e intercambien datos/valor, creando un ecosistema verdaderamente cross-chain.",
        "Polkadot no es ‘una sola blockchain’, sino una arquitectura de red multicadena con seguridad compartida."
      ]
    },
    {
      heading: "Origen del proyecto: Gavin Wood y la visión cross-chain",
      text: [
        "Polkadot fue impulsado por Gavin Wood (cofundador y ex-CTO de Ethereum), con la idea de superar límites de escalabilidad y habilitar interoperabilidad real entre cadenas.",
        "El proyecto toma fuerza con Parity Technologies y la Web3 Foundation como actores centrales del ecosistema (desarrollo, investigación y soporte comunitario)."
      ]
    },
    {
      heading: "La idea clave: una red heterogénea (no una cadena única)",
      text: [
        "Polkadot fue diseñada para conectar cadenas heterogéneas: es decir, blockchains que pueden ser muy distintas (DeFi, identidad, gaming, privacidad, etc.).",
        "Esto permite que cada cadena optimice su propósito, sin obligar a toda la red a usar las mismas reglas o el mismo modelo de ejecución."
      ]
    },
    {
      heading: "Relay Chain: el corazón de Polkadot",
      text: [
        "La Relay Chain es la cadena principal de Polkadot: coordina la seguridad, el consenso y el ‘orden’ del sistema.",
        "En lugar de ejecutar todas las aplicaciones dentro de la cadena principal, Polkadot delega ejecución a cadenas paralelas y usa la Relay Chain como capa de coordinación y seguridad compartida."
      ]
    },
    {
      heading: "Parachains: cadenas paralelas que corren en conjunto",
      text: [
        "Las parachains son blockchains que se conectan a Polkadot y se ejecutan en paralelo.",
        "Cada parachain puede tener su propia lógica, tokens y smart contracts, mientras aprovecha la seguridad compartida y la interoperabilidad del ecosistema.",
        "La ejecución paralela permite escalar horizontalmente: más cadenas procesan más trabajo al mismo tiempo."
      ]
    },
    {
      heading: "Bridges y comunicación cross-chain",
      text: [
        "Polkadot puede conectar con otras redes mediante bridges (puentes), permitiendo interoperabilidad con ecosistemas externos.",
        "Además, la comunicación entre parachains se apoya en mensajería cross-chain dentro del ecosistema (la idea general: pasar mensajes/activos entre cadenas sin depender de una sola app)."
      ]
    },
    {
      heading: "Substrate: el kit para construir blockchains",
      text: [
        "Un punto fuerte de Polkadot es su stack de desarrollo: Substrate.",
        "Substrate permite construir blockchains modulares de forma más rápida, con componentes reutilizables (consenso, gobernanza, balances, etc.).",
        "Muchas parachains nacen gracias a Substrate por su enfoque ‘framework’."
      ]
    },

    // ===== Consenso / roles =====
    {
      heading: "NPoS: Nominated Proof of Stake",
      text: [
        "Polkadot utiliza Nominated Proof of Stake (NPoS), una variación de Proof of Stake enfocada en descentralización y representación proporcional.",
        "El diseño busca que los validadores del conjunto activo estén respaldados por nominaciones y que el stake se distribuya para reforzar la seguridad general."
      ]
    },
    {
      heading: "Roles de red: validadores, nominadores, collators y fishers",
      list: [
        "Validadores: producen y validan bloques y garantizan la seguridad; hacen staking de DOT.",
        "Nominadores: delegan su stake respaldando validadores y comparten recompensas (y riesgos).",
        "Collators (coladores): mantienen el estado de una parachain y proponen bloques candidatos para esa parachain.",
        "Fishermen (pescadores): monitorean comportamientos maliciosos y reportan evidencia para proteger el sistema."
      ]
    },

    // ===== Token DOT =====
    {
      heading: "Token DOT: para qué sirve",
      list: [
        "Gobernanza: votar decisiones del protocolo y del uso del tesoro.",
        "Staking: seguridad económica y recompensas para validadores/nominadores.",
        "Pago e incentivos: comisiones y mecánicas económicas del ecosistema.",
        "Acceso a capacidad de red (históricamente con slots y hoy con coretime en Polkadot 2.0)."
      ]
    },

    // ===== Gobernanza moderna =====
    {
      heading: "OpenGov: gobernanza más comunitaria",
      text: [
        "Polkadot lanzó OpenGov el 15 de junio de 2023, reemplazando el modelo anterior (más ‘representativo’) por un sistema más directo y comunitario.",
        "El objetivo es que las decisiones y el control del protocolo estén más centrados en la comunidad, con procesos on-chain más abiertos y flexibles."
      ]
    },

    // ===== Polkadot 2.0 =====
    {
      heading: "Polkadot 2.0: el cambio de ‘slots’ a ‘coretime’",
      text: [
        "Uno de los cambios más importantes rumbo a Polkadot 2.0 es pasar de rentar parachains con subastas de ‘slots’ (con DOT bloqueado por largos periodos) a un mercado de capacidad llamado coretime.",
        "Con coretime, los proyectos pueden comprar tiempo/capacidad de cómputo de la red de forma más flexible (por ejemplo, por periodos mensuales), sin requerir bloqueos largos como en las subastas.",
        "Las subastas on-chain se detuvieron con una actualización de runtime el 19 de septiembre de 2024, migrando leases existentes a coretime."
      ]
    },
    {
      heading: "Los 3 componentes típicos de Polkadot 2.0",
      text: [
        "En comunicación del ecosistema, Polkadot 2.0 suele describirse con tres innovaciones: Agile Coretime, Async Backing y Elastic Scaling.",
        "La idea general es aumentar rendimiento (más throughput), mejorar latencia/finalidad y ofrecer asignación de capacidad más eficiente para aplicaciones."
      ]
    },
    {
      heading: "Agile Coretime (explicado en simple)",
      text: [
        "Agile Coretime convierte el ‘espacio de bloque’ en un recurso que se puede comprar y renovar con más elasticidad.",
        "Esto permite a proyectos ajustar su consumo: pagar por capacidad cuando la necesitan, en lugar de rentar un ‘slot’ fijo por mucho tiempo."
      ]
    },

    // ===== JAM (visión futuro) =====
    {
      heading: "JAM y la visión de ‘Polkadot como supercomputadora Web3’",
      text: [
        "Dentro del ecosistema Polkadot también existe una visión a más largo plazo conocida como JAM, presentada en el Gray Paper.",
        "La narrativa: evolucionar hacia un modelo tipo ‘world computer’ altamente performante, orientado a cómputo y servicios cloud Web3, con alta capacidad de procesamiento e I/O."
      ]
    },

    // ===== Ventajas / retos =====
    {
      heading: "Ventajas de Polkadot",
      list: [
        "Arquitectura multicadena (paralelismo real).",
        "Interoperabilidad: comunicación entre cadenas dentro del ecosistema.",
        "Seguridad compartida: parachains heredan seguridad del conjunto de validadores.",
        "Substrate: construir cadenas modulares con herramientas maduras.",
        "Evolución del modelo económico (coretime) para hacer el blockspace más eficiente."
      ]
    },
    {
      heading: "Retos y críticas comunes",
      list: [
        "Complejidad: entender Relay Chain + parachains + mensajería cross-chain requiere curva de aprendizaje.",
        "Economía y coordinación: el éxito depende de adopción real de parachains y del mercado de coretime.",
        "Puentes: como en todo Web3, los bridges suelen ser puntos de riesgo si no están bien diseñados.",
        "Experiencia de usuario: al ser multicadena, el onboarding puede ser más difícil si no hay buena UX."
      ]
    },

    // ===== Buenas prácticas =====
    {
      heading: "Buenas prácticas si usas DOT o apps del ecosistema",
      list: [
        "Verifica si estás interactuando con Relay Chain o con una parachain específica.",
        "Confirma redes, direcciones y puentes antes de mover fondos (haz pruebas pequeñas).",
        "Revisa comisiones y requisitos (staking, mínimos, tiempos de desbloqueo).",
        "Usa wallets y dApps oficiales o muy reputadas; evita enlaces dudosos."
      ]
    },

    // ===== Cierre =====
    {
      heading: "Conclusión",
      text: [
        "Polkadot busca resolver dos grandes desafíos de Web3: escalabilidad y comunicación entre cadenas.",
        "Su modelo Relay Chain + parachains habilita ejecución paralela y seguridad compartida.",
        "Con Polkadot 2.0 y el cambio a coretime, el proyecto apunta a un mercado de capacidad más flexible y eficiente.",
        "Entender Polkadot es entender el futuro multicadena: muchas cadenas especializadas trabajando juntas, en lugar de una sola cadena para todo."
      ]
    }
  ]
};

export default CAP10;
